//@ts-check

import { SlideshowAssetLoader } from "./SlideshowAssetLoader";
import { SlideScaler } from "./SlideScaler";
import { SlideCycler } from "./SlideCycler";
import { SlideshowNavigation } from "./SlideshowNavigation";
import { slideshowConductor } from "./SlideshowConductor";
import debug from "debug";

const log = debug("slideshow:Slideshow");

// Transition speed (ms) when the user skips via arrows or keyboard.
// - when null, the normal speed will be used
const MANUAL_SPEED = null;

// Instantiated via slideshow_controller.js
export class Slideshow {
  /**
   * @param {HTMLElement} element
   */
  constructor(element) {
    this.element = element;
  }

  init() {
    log("init", this.element);
    const assetLoader = new SlideshowAssetLoader(this.element);
    this.assetLoader = assetLoader;
    this.scaler = new SlideScaler(this.element);

    const slides = /** @type {HTMLElement[]} */ (
      Array.from(this.element.querySelectorAll(":scope > .slideshow-slide"))
    );
    this.cycler = new SlideCycler(slides, {
      fx: /** @type {import("./SlideCycler").Fx} */ (
        this.element.dataset.slideshowFx || "scrollHorz"
      ),
      // Prefetch the assets of the slide after the one coming up.
      onBefore: (incoming) => {
        this._updateArrowColor(incoming);
        assetLoader.loadForSlideElement(incoming.nextElementSibling);
      },
    });

    this.element.style.display = "block";
    this.assetLoader.loadInitial();

    if (slides[0]) this._updateArrowColor(slides[0]);

    this.scaler.start();

    this.navigation = new SlideshowNavigation(this.element, slideshowConductor);
    this.navigation.init();
    slideshowConductor.register(this);
  }

  get speed() {
    return parseInt(this.element.dataset.slideshowSpeed ?? "800");
  }

  // False in the admin widget preview (no arrows rendered).
  get navigationEnabled() {
    return this.element.dataset.slideshowNavigation !== "false";
  }

  /**
   * Called by SlideshowConductor: quick for user skips, the normal speed for
   * the automatic ones.
   * @param {"next" | "prev"} direction
   * @param {{ manual: boolean }} options
   */
  step(direction, { manual }) {
    if (!this.cycler) return;

    const speed = manual && MANUAL_SPEED !== null ? MANUAL_SPEED : this.speed;
    // May be shorter than requested (impatient mode); null if ignored.
    const duration = this.cycler.step(direction, { speed, manual });
    if (duration === null) return;

    // Keeps the arrow color fade in step with the slide animation.
    this.element.style.setProperty("--slideshow-speed", `${duration}ms`);
  }

  /**
   * Arrows sit outside the slides, so they're colored for the incoming slide:
   * white on a dark background color, black otherwise. Background images are
   * ignored. The computed style normalizes any CSS color to rgb()/rgba().
   * @param {HTMLElement} slideEl
   */
  _updateArrowColor(slideEl) {
    const bg = slideEl.querySelector(".slide-bg");
    if (!bg) return;

    const backgroundColor = getComputedStyle(bg).backgroundColor;
    const [r, g, b, alpha = 1] = (backgroundColor.match(/[\d.]+/g) ?? []).map(
      Number,
    );
    if (r === undefined) return;

    // Fully transparent: the (white) page shows through.
    if (alpha === 0) {
      this.element.style.setProperty("--arrow-color", "black");
      return;
    }

    // Perceived brightness (ITU-R BT.601 weights), 0..255.
    const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
    this.element.style.setProperty(
      "--arrow-color",
      brightness < 128 ? "white" : "black",
    );
  }

  destroy() {
    log("destroy", this.element);
    slideshowConductor.unregister(this);
    this.navigation?.destroy();
    this.navigation = null;

    this.cycler?.destroy();
    this.cycler = null;

    this.scaler?.stop();
    this.scaler = null;
    this.assetLoader = null;
  }
}
